import type { C1_Room } from "./C1_Room";
import { C1_Wall } from "./C1_Wall";
import { polygonContainsPoint } from "../lib/C1_polygonPlacement";
import { type RoomPlacementPlan } from "../lib/C1_roomSubtraction";
import type { C1_PointData } from "../types/C1_ModelTypes";
import type BaseModel from "../../../src/models/BaseModel";

/** Preserve surviving wall identities and visibility when a simple polygon receives an edge cut. */
export function replaceRoomPolygon(room: C1_Room, polygon: C1_PointData[]): void {
  if (!room.owner || !room.walls) throw new Error("Raum ohne Wandkontur.");
  const old = [...room.walls].map(wall => ({ wall, points: room.wallPoints(wall)! }));
  const used = new Set<C1_Wall>();
  const boundary = polygon.map((start, i) => {
    const end = polygon[(i + 1) % polygon.length], dx = end.x - start.x, dy = end.y - start.y;
    const length = Math.hypot(dx, dy);
    const source = old.find(({ points }) => {
      const onLine = (p: C1_PointData) => Math.abs(dx * (p.y - start.y) - dy * (p.x - start.x)) / length < .001;
      const along = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
      return onLine(points.start) && onLine(points.end) &&
        Math.min(Math.max(start[along], end[along]), Math.max(points.start[along], points.end[along])) -
        Math.max(Math.min(start[along], end[along]), Math.min(points.start[along], points.end[along])) > .001;
    });
    let wall = source && !used.has(source.wall) ? source.wall : undefined;
    if (!wall) { wall = new C1_Wall({ end, visible: source?.wall.visible ?? true }, room.owner); room.walls!.add(wall); }
    used.add(wall);
    return { wall, start, end };
  });
  room.adoptWallBoundary(boundary);
  for (const { wall } of old) if (!used.has(wall)) wall.delete();
}

/** Create and cut in one native undo transaction; materialization starts only after validation. */
export function applyRoomPlacement(plan: RoomPlacementPlan<C1_Room>, create: () => C1_Room): C1_Room {
  const contents = plan.cuts.map(({ room }) => ({ room,
    openings: room.openings.map(item => ({ item, point: item.absolutePosition })),
    furniture: room.furnitures.map(item => ({ item, point: item.equipmentPostitionGeometric(item.id), docked: !!item.dockWall })),
    devices: room.interactiveEloItems.map(item => ({ item, point: item.independentAbsolutePosition })),
  }));
  const added = create();
  for (const { room, polygon } of plan.cuts) replaceRoomPolygon(room, polygon);
  added.recalculateFloorWallTypes();
  const target = (source: C1_Room, point: C1_PointData) => polygonContainsPoint(added.polygonPoints(), point) ? added : source;
  const transfer = (owner: BaseModel | undefined, source: C1_Room, destination: C1_Room) => {
    if (destination === source || !owner || !destination.owner) return;
    if (!owner.moveToNewParent(destination.owner)) throw new Error("Die Ausstattung konnte nicht dem neuen Raum zugeordnet werden.");
  };
  for (const { room, openings, furniture, devices } of contents) {
    for (const { item, point } of openings) {
      const destination = target(room, point);
      transfer(item.owner as BaseModel | undefined, room, destination);
      const placement = destination.nearestWallPlacementInRoom(point, item.width, false);
      const wall = placement && destination.walls?.findFirst(candidate => candidate.id === placement.wallId);
      if (wall && placement) item.setWallPlacement(wall, placement.offset, item.reflect);
    }
    for (const { item, point, docked } of furniture) {
      if (!point) continue;
      const destination = target(room, point);
      transfer(item.owner as BaseModel | undefined, room, destination);
      const rect = destination.innerRect();
      const placement = docked ? destination.nearestWallPlacementInRoom(point, 0, false) : undefined;
      item.setPosition({ x: (point.x - rect.x1) / (rect.x2 - rect.x1), y: (point.y - rect.y1) / (rect.y2 - rect.y1) },
        placement?.side, item.reflect, placement ? item.wallDocked : undefined, placement?.wallId);
    }
    for (const { item, point } of devices) {
      if (!point) continue;
      const destination = target(room, point);
      transfer(item.owner as BaseModel | undefined, room, destination);
      item.moveInteractiveTo(point);
    }
  }
  return added;
}
